import { Spinner } from '@awell/ui-kit/components/Spinner'
import React, { useState } from 'react'
import { t } from 'i18next'
import { SubHeading } from '@awell/ui-kit/components/Typography'
import { ActivityList, SidePanelCardVariants } from '@awell/ui-kit/components/ActivityList'
import { isNil } from 'lodash'
import { StepPanelHeader } from '@awell/ui-kit/components/StepFeedPanel'
import { spacing } from '@awell/ui-kit/utils/style-guide'
import { PathwaySidePanelLayout } from '../../components/PathwaySidePanelLayout'
import { usePathwayContextProvider } from '../../components/PathwayContextProvider'
import { ActivityObjectType, ElementStatus, ElementType } from './types'
import { useStopTrack } from '../../hooks/useStopTrack'
import ActivityDetailsHeader from './ActivityDetails/ActivityDetailsHeader'
import { type Activity } from '../../components/ActivityDetails/types'
import { AlertDialog, Button, Dropdown, Icon, Tooltip } from '@awell-health/design-system'
import { ActivityDetailsRenderer } from '../../components/ActivityDetails/ActivityDetailsRenderer'
import { useRetryActivity } from '../../hooks/useRetryActivity'
import { useRetryPushToEmr } from '../../hooks/useRetryPushToEmr'

export const PathwaySidePanel = (): JSX.Element => {
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const { stopTrack } = useStopTrack()
  const {
    onSelectActivity,
    onSelectElement,
    selectedActivity,
    onClosePanel,
    pendingActivities,
    loading,
    selectedElement,
    filteredActivities
  } = usePathwayContextProvider()

  const { retryActivity } = useRetryActivity(selectedActivity?.id ?? '')
  const { retryPushToEmr } = useRetryPushToEmr(selectedActivity?.id ?? '')

  if (loading) {
    return <Spinner />
  }

  const openStopTrackConfirmationModal = (): void => {
    setModalOpen(true)
  }
  const handleModalClose = (): void => {
    setModalOpen(false)
  }

  const handleStopTrack = async (): Promise<void> => {
    if (!isNil(selectedElement)) {
      await stopTrack({
        pathway_id: selectedElement.context.pathway_id,
        track_id: selectedElement.id
      })
    }
    onSelectActivity(undefined)
    onSelectElement(undefined)
    handleModalClose()
    onClosePanel()
  }

  const showingActivity = !isNil(selectedActivity)
  const showingFilteredActivities = !isNil(selectedElement) && isNil(selectedActivity)

  const getAdditionalActionButtons = (): JSX.Element | undefined => {
    if (showingActivity) {
      const copyUrl = getCopyUrl()
      const items = [
        {
          label: (
            <div className="flex items-center gap-2">
              <Icon icon="RiLink" size={16} className="fill-slate-600" />
              Copy to clipboard
            </div>
          ),
          onClick: () => {
            void navigator.clipboard.writeText(copyUrl)
          }
        }
      ]

      const showRetryActivityButton = [ActivityObjectType.PluginAction, ActivityObjectType.ApiCall].includes(
        selectedActivity.object.type
      )

      if (selectedActivity.object.type === ActivityObjectType.EmrReport) {
        items.push({
          label: (
            <div className="flex items-center gap-2">
              <Icon icon="RiRefreshLine" size={16} className="fill-slate-600" />
              Resend report
            </div>
          ),
          onClick: async () => {
            await retryPushToEmr()
          }
        })
      }
      if (showRetryActivityButton) {
        items.push({
          label: (
            <div className="flex items-center gap-2">
              <Icon icon="RiRefreshLine" size={16} className="fill-slate-600" />
              Retry
            </div>
          ),
          onClick: () => {
            retryActivity()
          }
        })
      }
      return (
        <Dropdown
          buttonClassNames="bg-white btn-square h-9 w-9 min-h-9 min-w-h-9 rounded-lg hover:bg-slate-100"
          buttonLabel={<Icon icon="RiMore2Line" size={20} className="fill-slate-700" />}
          itemClassNames="hover:text-blue-700 hover:bg-blue-50 hover:fill-blue-700"
          items={items}
          placement="left"
        />
      )
    } else if (selectedElement?.type === ElementType.Track && selectedElement.status === ElementStatus.Active) {
      return (
        <Tooltip datatip="Stop track">
          <Button
            variant="ghost"
            size={null}
            shape="squareSm"
            onClick={() => {
              openStopTrackConfirmationModal()
            }}
          >
            <Icon icon="RiStopCircleFill" size={24} className="fill-slate-600" />
          </Button>
        </Tooltip>
      )
    }
  }

  const getCopyUrl = (): string => {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    if (isNil(selectedActivity)) {
      params.delete('activityId')
      url.search = params.toString()
      return url.toString()
    }
    params.set('activityId', selectedActivity.id)
    url.search = params.toString()
    return url.toString()
  }

  return (
    <>
      <PathwaySidePanelLayout showTabs={showingActivity} actionButtons={getAdditionalActionButtons()}>
        <PathwaySidePanelLayout.Header>
          {showingFilteredActivities && selectedElement.type === ElementType.Step && (
            <StepPanelHeader element={selectedElement} />
          )}
          {showingFilteredActivities && selectedElement.type === ElementType.Track && (
            <div style={{ marginTop: spacing.xxs }}>
              <SubHeading>{selectedElement.name}</SubHeading>
            </div>
          )}
          {showingActivity && <ActivityDetailsHeader activity={selectedActivity} />}
          {!showingFilteredActivities && !showingActivity && (
            <div style={{ marginTop: spacing.xxs }}>
              <SubHeading>{t('pending_activities_title')}</SubHeading>
            </div>
          )}
        </PathwaySidePanelLayout.Header>
        <PathwaySidePanelLayout.Content>
          {showingActivity && (
            <ActivityDetailsRenderer activity={selectedActivity as Activity} onClose={onClosePanel} />
          )}
          {!showingActivity && (
            <ActivityList
              // @ts-expect-error fix types
              activities={showingFilteredActivities ? filteredActivities : pendingActivities}
              // @ts-expect-error fix types
              onSelectActivity={onSelectActivity}
              variant={showingFilteredActivities ? SidePanelCardVariants.Step : SidePanelCardVariants.Pending}
            />
          )}
        </PathwaySidePanelLayout.Content>
      </PathwaySidePanelLayout>
      {modalOpen && (
        <AlertDialog
          button={{
            buttonAction: handleStopTrack,
            buttonVariant: 'error',
            label: 'Stop'
          }}
          secondaryButton={{
            buttonAction: handleModalClose,
            label: 'Cancel'
          }}
          onClose={handleModalClose}
          icon={<Icon icon="RiErrorWarningFill" size={24} className="fill-red-600" />}
          title={t('stop_track_title', {
            trackName: selectedElement?.name ?? 'this track'
          })}
          text={t('stop_track_explanation')}
        ></AlertDialog>
      )}
    </>
  )
}
